/**
 * AP-46 11.2 — **Akte der Baustellendokumente** im Listen-Standard: FilterBar, Spalten über
 * `ui-settings`, Sortierung, Seiten, Export samt Mail. Nummern, die laut Herzschlag noch auf einem
 * Gerät liegen, stehen als eigene, blasse Zeilen oben (BD-35).
 *
 * Paginiert, gefiltert und sortiert auf dem Server. Muster: `CalculationsPage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Plus, Settings } from 'lucide-react'
import { SITE_DOC_KINDS, SITE_DOC_KIND_MODULE, SITE_DOC_KIND_PERMISSION, SITE_DOC_STATUSES, erfuelltRecht, type SiteDocListRow, type TagebuchListenWerte } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { ExportButton } from '@/components/ui/ExportButton'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { useAuthStore } from '@/lib/auth.store'
import { fmtDate, fmtDateTime } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology } from '@/lib/useTerminology'
import { fetchSiteDocs, regieLinesExportRequest, siteDocsExportRequest, type ListSiteDocsParams, type SiteDocListe } from '../api/site-docs.api'
import { NeuesDokumentDialog } from '../components/NeuesDokumentDialog'
import { StatusPill } from '../components/StatusPill'

const LAYOUT_KEY = 'columnbar_layout_site_docs'

interface ColDef {
  key: string
  label: string
  sortable?: boolean
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

const DEFAULT_COLUMN_KEYS = ['kind', 'documentDate', 'title', 'projectLabel', 'status', 'createdByName', 'signedBy', 'hours', 'billing']
const DEFAULT_FILTER_FIELD_KEYS = ['q', 'kind', 'status', 'from', 'to']
const STANDARD: ColumnState = { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'documentDate', sortDir: 'desc' }

function filterParams(filters: Record<string, ActiveFilter>): ListSiteDocsParams {
  const p: ListSiteDocsParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'q') p.q = a.value
    if (key === 'kind') p.kind = a.value
    if (key === 'status') p.status = a.value
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
    if (key === 'copyOpen' && a.value === 'true') p.copyOpen = true
    if (key === 'filesMissing' && a.value === 'true') p.filesMissing = true
    if (key === 'awaiting' && a.value === 'true') p.awaiting = true
    if (key === 'notFetched' && a.value === 'true') p.notFetched = true
    if (key === 'onDevice') p.onDevice = a.value === 'true'
    if (key === 'billing') p.billing = a.value as ListSiteDocsParams['billing']
  }
  return p
}

export default function SiteDocsListPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const navigate = useNavigate()
  const { user, hasModule } = useAuthStore()
  const mitBelegwesen = hasModule('CORRESPONDENCE')
  const darfRegieExport = hasModule('SITE_DOCS.REGIE') && erfuelltRecht('sitedocs.regie.bill', user?.permissions)
  const [nurNeue, setNurNeue] = useState(true)
  const darfAnlegen = SITE_DOC_KINDS.some((k) => hasModule(SITE_DOC_KIND_MODULE[k]) && erfuelltRecht(SITE_DOC_KIND_PERMISSION[k], user?.permissions))

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: 'q', label: t('siteDocs.filter.q'), type: 'text', category: 'core', operators: ['contains'] },
      { key: 'kind', label: t('siteDocs.filter.kind'), type: 'select-multi', category: 'core', operators: ['is'], options: SITE_DOC_KINDS.map((k) => ({ value: k, label: t(`siteDocs.kinds.${k}`) })) },
      { key: 'status', label: t('siteDocs.filter.status'), type: 'select-multi', category: 'core', operators: ['is'], options: SITE_DOC_STATUSES.map((s) => ({ value: s, label: t(`siteDocs.status.${s}`) })) },
      { key: 'from', label: t('siteDocs.filter.from'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'to', label: t('siteDocs.filter.to'), type: 'date', category: 'core', operators: ['is'] },
      { key: 'onDevice', label: t('siteDocs.filter.onDevice'), type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'awaiting', label: t('siteDocs.filter.awaiting'), type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'copyOpen', label: t('siteDocs.filter.copyOpen'), type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'filesMissing', label: t('siteDocs.filter.filesMissing'), type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'notFetched', label: t('siteDocs.filter.notFetched'), type: 'boolean', category: 'core', operators: ['is'] },
      // AP-27 7.: Abrechnungsstand — nur mit Regiebericht.
      ...(hasModule('SITE_DOCS.REGIE')
        ? [
            {
              key: 'billing',
              label: t('siteDocs.filter.billing'),
              type: 'select',
              category: 'core',
              operators: ['is'],
              // Ohne Belegwesen (AP-27 19.): offen · exportiert — abgeholt hat seinen eigenen Filter.
              options: (hasModule('CORRESPONDENCE') ? (['BILLABLE', 'OPEN', 'PARTIAL', 'BILLED'] as const) : (['NOT_EXPORTED', 'EXPORTED'] as const)).map((v) => ({ value: v, label: t(`siteDocs.filter.billingValues.${v}`) })),
            } satisfies FilterFieldDef,
          ]
        : []),
    ],
    [t, hasModule],
  )
  const alleSpalten: ColDef[] = useMemo(
    () => [
      { key: 'kind', label: t('siteDocs.columns.kind'), sortable: true },
      { key: 'documentDate', label: t('siteDocs.columns.date'), sortable: true },
      { key: 'title', label: t('siteDocs.columns.title'), sortable: true },
      { key: 'projectLabel', label: terms.project, sortable: true },
      { key: 'status', label: t('siteDocs.columns.status'), sortable: true },
      { key: 'createdByName', label: t('siteDocs.columns.createdBy') },
      { key: 'signedBy', label: t('siteDocs.columns.signedBy') },
      { key: 'receivedAt', label: t('siteDocs.columns.receivedAt') },
      { key: 'copy', label: t('siteDocs.columns.copy') },
      { key: 'files', label: t('siteDocs.columns.files') },
      { key: 'fetchedAt', label: t('siteDocs.columns.fetchedAt') },
      // AP-27 9.: Spalten des Regieberichts — leer bei anderen Arten.
      { key: 'hours', label: t('siteDocs.columns.hours') },
      { key: 'billing', label: t('siteDocs.columns.billing') },
      // AP-47 11.2: Spalten des Montageprotokolls — leer bei anderen Arten.
      { key: 'scope', label: t('siteDocs.columns.scope') },
      { key: 'acceptance', label: t('siteDocs.columns.acceptance') },
      { key: 'openDefects', label: t('siteDocs.columns.openDefects') },
      // AP-48 7.: Spalten des Bautagesberichts (`kindValues`) — leer bei anderen Arten.
      { key: 'persons', label: t('siteDocs.columns.persons') },
      { key: 'hindrance', label: t('siteDocs.columns.hindrance') },
      { key: 'standstill', label: t('siteDocs.columns.standstill') },
      { key: 'countersigned', label: t('siteDocs.columns.countersigned') },
      { key: 'objection', label: t('siteDocs.columns.objection') },
      // AP-49 11.1: Spalten der Projektbesprechung — leer bei anderen Arten.
      { key: 'meetingSeries', label: t('siteDocs.columns.meetingSeries') },
      { key: 'openTasks', label: t('siteDocs.columns.openTasks') },
      { key: 'distributed', label: t('siteDocs.columns.distributed') },
    ],
    [t, terms.project],
  )

  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, filterFields))
  const ladeSpalten = (): ColumnState => {
    const g = loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null)
    return g?.columnKeys?.length ? { columnKeys: g.columnKeys, columnWidths: g.columnWidths ?? {}, sortKey: g.sortKey ?? 'documentDate', sortDir: g.sortDir ?? 'desc' } : STANDARD
  }

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [result, setResult] = useState<SiteDocListe | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [anlegen, setAnlegen] = useState(false)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, seite: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        setResult(await fetchSiteDocs({ ...filterParams(filters), page: seite, limit: 50, sort: state.sortKey, dir: state.sortDir }))
      } catch {
        setError(t('siteDocs.list.loadError'))
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: Math.max(60, startW + ev.clientX - startX) } }))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))

  const renderCell = (z: SiteDocListRow, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'kind':
        return t(`siteDocs.kinds.${z.kind}`)
      case 'documentDate':
        return fmtDate(z.documentDate)
      case 'title':
        return z.title
      case 'projectLabel':
        return z.projectLabel || '—'
      case 'status':
        return <StatusPill status={z.status} amGeraet={z.onDevice} />
      case 'createdByName':
        return z.createdByName
      case 'signedBy':
        return z.signedBy ?? ''
      case 'receivedAt':
        return z.receivedAt ? fmtDateTime(z.receivedAt) : ''
      case 'copy':
        return z.copySentAt ? t('siteDocs.list.copySent', { datum: fmtDate(z.copySentAt) }) : z.copyEmail ? <span className="text-amber-700 dark:text-amber-300">{t('siteDocs.list.copyOpen')}</span> : ''
      case 'files':
        return z.filesComplete ? '' : <span className="text-destructive">{t('siteDocs.list.filesMissing')}</span>
      case 'fetchedAt':
        return z.fetchedAt ? fmtDateTime(z.fetchedAt) : ''
      case 'hours': {
        const h = z.kindValues?.hours as number | undefined
        return h ? `${String(h).replace('.', ',')} h` : ''
      }
      case 'billing': {
        const w = z.kindValues as { lines?: number; billedLines?: number; exportedAt?: string | null } | undefined
        if (!w?.lines) return ''
        // Ohne Belegwesen (AP-27 19.): offen · exportiert · abgeholt.
        if (!mitBelegwesen) return z.fetchedAt ? t('siteDocs.list.fetched') : w.exportedAt ? t('siteDocs.list.exported', { datum: fmtDate(w.exportedAt) }) : t('siteDocs.list.billedNone')
        return w.billedLines === w.lines ? t('siteDocs.list.billedAll') : w.billedLines ? t('siteDocs.list.billedSome', { anzahl: w.billedLines, von: w.lines }) : t('siteDocs.list.billedNone')
      }
      case 'scope':
        return z.kind === 'ASSEMBLY' && z.kindValues?.umfang ? t(`siteDocs.assembly.scopes.${String(z.kindValues.umfang)}`) : ''
      case 'acceptance':
        return z.kind === 'ASSEMBLY' && z.kindValues?.ergebnis ? t(`siteDocs.assembly.acceptanceResults.${String(z.kindValues.ergebnis)}`) : ''
      case 'openDefects': {
        const n = z.kind === 'ASSEMBLY' ? Number(z.kindValues?.openDefects ?? 0) : 0
        return n ? <span className="text-amber-700 dark:text-amber-300">{n}</span> : ''
      }
      case 'persons': {
        const p = z.kind === 'DIARY' ? (z.kindValues as Partial<TagebuchListenWerte> | undefined)?.persons : undefined
        return p ? String(p) : ''
      }
      case 'hindrance':
      case 'standstill':
      case 'countersigned':
      case 'objection': {
        const w = z.kind === 'DIARY' ? (z.kindValues as Partial<TagebuchListenWerte> | undefined) : undefined
        if (!w?.[col.key]) return ''
        return col.key === 'objection' ? <span className="font-medium text-amber-700 dark:text-amber-300">{t('siteDocs.list.objection')}</span> : t('siteDocs.evidence.yes')
      }
      case 'meetingSeries': {
        const w = z.kindValues as { meetingType?: string; sequence?: number } | undefined
        return z.kind === 'MEETING' && w?.meetingType ? `${w.meetingType} Nr. ${w.sequence}` : ''
      }
      case 'openTasks': {
        const n = z.kind === 'MEETING' ? (z.kindValues?.openTasks as number | undefined) : undefined
        return n ? String(n) : ''
      }
      case 'distributed': {
        if (z.kind !== 'MEETING' || z.status !== 'FINAL') return ''
        const w = z.kindValues as { sentAt?: string | null } | undefined
        return w?.sentAt ? t('siteDocs.list.distributedOn', { datum: fmtDate(w.sentAt) }) : <span className="text-amber-700 dark:text-amber-300">{t('siteDocs.list.notDistributed')}</span>
      }
      default:
        return null
    }
  }

  const geraete = page === 1 ? result?.onDevice ?? [] : []
  const rows = [...geraete, ...(result?.data ?? [])]
  const meta = result?.meta
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const pfeil = (key: string) =>
    colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('siteDocs.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('siteDocs.list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            request={(format, orientation) =>
              siteDocsExportRequest({ ...filterParams(filterValues), sort: colState.sortKey, dir: colState.sortDir, format, orientation }, [{ key: 'number', label: t('siteDocs.columns.number') }, ...shownColumns.map((c) => ({ key: c.key, label: c.label }))])
            }
            mail={{ source: 'EXPORT_SITE_DOCUMENTS', title: t('mailExport.titles.EXPORT_SITE_DOCUMENTS'), subject: t('siteDocs.title') }}
          />
          {/* AP-27 19.: die Regiezeilen für ein fremdes ERP — Zeitraum aus dem Filter, „exportiert am" als Hinweis. */}
          {darfRegieExport && (
            <ExportButton
              label={t('siteDocs.list.regieExport')}
              defaultFilename="regiezeilen"
              request={(format, orientation) => {
                const f = filterParams(filterValues)
                return regieLinesExportRequest({ from: f.from, to: f.to, notExported: nurNeue || undefined, format, orientation })
              }}
              extra={
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={nurNeue} onChange={(e) => setNurNeue(e.target.checked)} />
                  {t('siteDocs.list.regieExportNew')}
                </label>
              }
              mail={{ source: 'EXPORT_SITE_REPORT_LINES', title: t('mailExport.titles.EXPORT_SITE_REPORT_LINES'), subject: t('mailExport.titles.EXPORT_SITE_REPORT_LINES') }}
            />
          )}
          {darfAnlegen && (
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={() => setAnlegen(true)}>
              <Plus className="h-4 w-4" /> {t('siteDocs.list.new')}
            </button>
          )}
        </div>
      </div>

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey="site_docs" defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS} adresse={adresse} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title={t('siteDocs.list.columns')}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? STANDARD
                markUiSettingApplied(LAYOUT_KEY, next)
                setColState(next)
                void fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="relative px-4 py-1.5 text-left font-medium text-foreground" style={colState.columnWidths.number ? { width: colState.columnWidths.number } : undefined}>
                  <button type="button" onClick={() => handleSort('number')} className="flex items-center gap-1 hover:text-primary">
                    {t('siteDocs.columns.number')} {pfeil('number')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, 'number')} />
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 text-left font-medium text-foreground ${columnConfigMode ? 'cursor-grab' : ''} ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => { e.preventDefault(); setDragOverKey(col.key) } : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.sortable ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label} {pfeil(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive" title={t('siteDocs.list.removeColumn')}>
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">{t('siteDocs.list.addColumn')}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('siteDocs.list.empty')}
                  </td>
                </tr>
              )}
              {rows.map((z, idx) => (
                <tr
                  key={z.onDevice ? `g-${z.id}` : z.id}
                  onClick={z.onDevice ? undefined : () => navigate(`/site-docs/${z.id}`)}
                  title={z.onDevice ? t('siteDocs.list.onDeviceHint') : undefined}
                  className={`border-b border-border last:border-0 ${z.onDevice ? 'italic text-muted-foreground' : 'cursor-pointer'} ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}
                >
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{z.number ?? '—'}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5">
                      {renderCell(z, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">{t('siteDocs.list.count', { count: meta?.total ?? rows.length })}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="px-4 py-1.5" />
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>{t('siteDocs.list.page', { page: meta.page, pages: meta.totalPages, total: meta.total })}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {anlegen && <NeuesDokumentDialog onClose={() => setAnlegen(false)} onAngelegt={(id) => navigate(`/site-docs/${id}`)} />}
    </div>
  )
}
